import moment from "moment-timezone";
import { z } from "zod";
import { SchemaConfig, FieldMetaMap } from "@/lib/form-v2";
import { CommodityOptions } from "@/lib/api/commodity";

// ---------------------------------------------------------------------------
// Discriminator
// ---------------------------------------------------------------------------

export const MERCH_VALUE_TYPE = "merch_value" as const;

// ---------------------------------------------------------------------------
// Form values type
// ---------------------------------------------------------------------------

export interface MerchValueFormValues extends Record<string, unknown> {
  merch_value_type?: string;
  market_zone_id?: string;
  commodity?: string;
  crop_year?: number;
  note?: string;
  value?: string;
  range_start?: Date;
  range_end?: Date;
}

// ---------------------------------------------------------------------------
// Zod schema (base shape — used for introspection)
// ---------------------------------------------------------------------------

export const MerchValueBaseShape = z.object({
  merch_value_type: z.literal(MERCH_VALUE_TYPE),
  market_zone_id: z.string().uuid("Please select a market zone"),
  commodity: z.string().min(1, "Commodity is required"),
  crop_year: z.number().int().positive("Crop year must be a positive integer"),
  note: z.string().optional().nullable(),
  value: z.string().optional().nullable(),
  range_start: z.string().min(1, "Start date is required"),
  range_end: z.string().min(1, "End date is required"),
});

// ---------------------------------------------------------------------------
// Zod validator (with superRefine — used for validation)
// ---------------------------------------------------------------------------

export const MerchValueValidator = MerchValueBaseShape.superRefine((data, ctx) => {
  if (data.range_start && data.range_end) {
    const start = moment(data.range_start, "YYYY-MM-DD", true);
    const end = moment(data.range_end, "YYYY-MM-DD", true);
    if (start.isValid() && end.isValid() && end.isSameOrBefore(start)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date",
        path: ["range_end"],
      });
    }
  }
});

// ---------------------------------------------------------------------------
// Schema config
// ---------------------------------------------------------------------------

export const merchValueSchemaConfig: SchemaConfig = {
  discriminatorField: "merch_value_type",
  base: MerchValueBaseShape as z.ZodObject<z.ZodRawShape>,
  variants: {
    [MERCH_VALUE_TYPE]: MerchValueBaseShape as z.ZodObject<z.ZodRawShape>,
  },
  validators: {
    [MERCH_VALUE_TYPE]: MerchValueValidator,
  },
  defaultVariantKey: MERCH_VALUE_TYPE,
};

// ---------------------------------------------------------------------------
// Serialize helper — Date → "YYYY-MM-DD" string
// ---------------------------------------------------------------------------

function serializeDateToYMD(val: unknown): unknown {
  if (val instanceof Date && !isNaN(val.getTime())) {
    return moment(val).format("YYYY-MM-DD");
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// Field meta builder
// ---------------------------------------------------------------------------

export function buildMerchValueFieldMeta(
  marketZoneOptions: { value: string; label: string }[],
): FieldMetaMap<MerchValueFormValues> {
  return {
    merch_value_type: {
      label: "Type",
      widget: "text",
      visible: () => false,
    },
    market_zone_id: {
      label: "Market Zone",
      widget: "select",
      options: marketZoneOptions,
    },
    commodity: {
      label: "Commodity",
      widget: "select",
      options: CommodityOptions.map((o) => ({ value: o.value, label: o.label })),
    },
    crop_year: {
      label: "Crop Year",
      widget: "int",
      placeholder: "e.g. 2026",
    },
    value: {
      label: "Value",
      widget: "text",
      placeholder: "e.g. -40K",
    },
    note: {
      label: "Note",
      widget: "text",
      placeholder: "Optional note",
    },
    range_start: {
      label: "Start Date",
      widget: "date",
      serializeForValidation: serializeDateToYMD,
    },
    range_end: {
      label: "End Date",
      widget: "date",
      serializeForValidation: serializeDateToYMD,
    },
  };
}

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

export const defaultMerchValueFormValues: MerchValueFormValues = {
  merch_value_type: MERCH_VALUE_TYPE,
  market_zone_id: undefined,
  commodity: undefined,
  crop_year: new Date().getFullYear(),
  note: undefined,
  value: undefined,
  range_start: undefined,
  range_end: undefined,
};


